import { useCallback, useEffect, useState } from "react";

import {
  type DashboardAdminKeystoreSpendingEntry,
  type DashboardKeystoreEntry,
  assignKeystoreEntryAsAdmin,
  createKeystoreEntryAsAdmin,
  deleteKeystoreEntryAsAdmin,
  getKeystoreSpendingAsAdmin,
  listKeystoreEntriesAsAdmin,
  unassignKeystoreEntryAsAdmin,
} from "../dashboard-client";

interface UseKeystoreState {
  entries: DashboardKeystoreEntry[];
  loading: boolean;
  error: string | null;
}

function currentMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  const end = now.toISOString().slice(0, 10);
  return { end, start };
}

export function useKeystore() {
  const [state, setState] = useState<UseKeystoreState>({
    entries: [],
    error: null,
    loading: false,
  });

  const [spendingMap, setSpendingMap] = useState<
    Map<string, DashboardAdminKeystoreSpendingEntry>
  >(new Map());
  const [spendingLoading, setSpendingLoading] = useState(false);
  const [spendingRange, setSpendingRange] = useState(currentMonthRange);
  const [spendingWarnings, setSpendingWarnings] = useState<string[]>([]);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, error: null, loading: true }));
    try {
      const { data } = await listKeystoreEntriesAsAdmin();
      setState({ entries: data.entries, error: null, loading: false });
    } catch (error) {
      setState((s) => ({
        ...s,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load keystore entries",
        loading: false,
      }));
    }
  }, []);

  const refreshSpending = useCallback(async (start: string, end: string) => {
    setSpendingLoading(true);
    setSpendingRange({ end, start });
    try {
      const { data } = await getKeystoreSpendingAsAdmin({
        query: { end, start },
      });
      const map = new Map<string, DashboardAdminKeystoreSpendingEntry>();
      for (const e of data.entries) {
        map.set(e.entry_id, e);
      }
      setSpendingMap(map);
      setSpendingWarnings(data.warnings);
    } catch {
      setSpendingWarnings(["Failed to load spending data"]);
    } finally {
      setSpendingLoading(false);
    }
  }, []);

  const refreshWithCurrentSpending = useCallback(async () => {
    await refresh();
    await refreshSpending(spendingRange.start, spendingRange.end);
  }, [refresh, refreshSpending, spendingRange.end, spendingRange.start]);

  useEffect(() => {
    void refresh();
    const { start, end } = currentMonthRange();
    void refreshSpending(start, end);
  }, [refresh, refreshSpending]);

  const create = useCallback(
    async (
      provider: string,
      key: string,
      label?: string,
      providerKeyId?: string,
    ) => {
      await createKeystoreEntryAsAdmin({
        body: { key, label, provider, providerKeyId },
      });
      await refreshWithCurrentSpending();
    },
    [refreshWithCurrentSpending],
  );

  const remove = useCallback(
    async (entryId: string) => {
      await deleteKeystoreEntryAsAdmin({ path: { entryId } });
      await refreshWithCurrentSpending();
    },
    [refreshWithCurrentSpending],
  );

  const assign = useCallback(
    async (entryId: string, organizationId: string) => {
      await assignKeystoreEntryAsAdmin({
        body: { organizationId },
        path: { entryId },
      });
      await refreshWithCurrentSpending();
    },
    [refreshWithCurrentSpending],
  );

  const unassign = useCallback(
    async (entryId: string) => {
      await unassignKeystoreEntryAsAdmin({ path: { entryId } });
      await refreshWithCurrentSpending();
    },
    [refreshWithCurrentSpending],
  );

  return {
    assign,
    create,
    entries: state.entries,
    error: state.error,
    loading: state.loading,
    refresh,
    refreshSpending,
    remove,
    spendingLoading,
    spendingMap,
    spendingWarnings,
    unassign,
  };
}
